import { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import * as AppPics from 'assets/images';
import { AppButton } from 'components';
import AppBreadcrumbs from 'components/molecules/AppBreadcrumbs';
import { AppModal, AppModalBody, AppModalHeader } from 'components/molecules/Modal/AppModal';
import { notifier } from 'components/Toaster';
import usePodcastSubscription from 'hooks/API/GET/usePodcastSubscription';
import useManagePodcastSubscription from 'hooks/API/useManagePodcastSubscription';

const STATUS_BADGE = {
  active: 'badge bg-success',
  pending: 'badge bg-warning',
  cancelled: 'badge bg-secondary',
  expired: 'badge bg-danger',
};

const REFUND_BADGE = {
  pending: 'badge bg-warning',
  refunded: 'badge bg-success',
};

const MODAL_TITLES = {
  cancel: 'Cancel subscription',
  cancelAndRefund: 'Cancel and refund',
  markRefunded: 'Mark refunded',
};

const MODAL_NOTES = {
  cancel: 'Auto-renewal stops now. The user keeps access until the end of the paid period.',
  cancelAndRefund:
    'Access ends now and auto-renewal stops. Refund the latest payment manually, then mark it refunded.',
  markRefunded: 'Enter the reference of the refund you made manually.',
};

const formatDate = (iso, withTime = false) =>
  iso && new Date(iso).getFullYear() > 2000
    ? new Date(iso).toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        ...(withTime && { hour: '2-digit', minute: '2-digit' }),
      })
    : '—';

const formatAmount = (amountInPaise, currency = 'INR') =>
  `${currency === 'INR' ? '₹' : currency} ${(Number(amountInPaise || 0) / 100).toLocaleString(
    'en-IN'
  )}`;

function SubscriptionDetail() {
  const { subscriptionId } = useParams();
  const history = useHistory();

  const [modal, setModal] = useState({ action: null, payment: null });
  const [text, setText] = useState('');
  const [error, setError] = useState('');

  const detailQuery = usePodcastSubscription({ enabled: true, subscriptionId });
  const { mutateAsync, isLoading: saving } = useManagePodcastSubscription();

  const detail = detailQuery.data?.data?.data || null;
  const subscription = detail?.subscription;
  const user = detail?.user;
  const payments = detail?.payments || [];
  const isCancellable = ['active', 'pending'].includes(subscription?.status);

  const openModal = (action, payment = null) => {
    setText('');
    setError('');
    setModal({ action, payment });
  };

  const closeModal = () => setModal({ action: null, payment: null });

  const submit = async () => {
    const value = text.trim();
    if (!value) {
      setError(
        modal.action === 'markRefunded' ? 'Refund reference is required.' : 'Reason is required.'
      );
      return;
    }
    try {
      await mutateAsync(
        modal.action === 'markRefunded'
          ? { action: 'markRefunded', paymentId: modal.payment._id, refundReference: value }
          : { action: modal.action, subscriptionId, reason: value }
      );
      notifier.success(`${MODAL_TITLES[modal.action]} done`);
      closeModal();
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Something went wrong.');
    }
  };

  const infoRows = subscription
    ? [
        ['Name', user?.name],
        ['Email', user?.email || user?.optionalEmail],
        ['Mobile', user?.mobile ? `${user.countryCode || ''} ${user.mobile}` : null],
        ['Plan', subscription.plan],
        ['Status', subscription.status],
        ['Auto renew', isCancellable ? 'On' : 'Off'],
        ['Started', formatDate(subscription.createdAt)],
        ['Access until', formatDate(subscription.currentPeriodEnd)],
        ['Cancelled on', formatDate(subscription.cancelledAt)],
        ['Cancel reason', subscription.cancelReason],
      ]
    : [];

  return (
    <div className="mainWraper coursetabsection">
      <div className="tab">
        <ul className="tab__menu">
          <li className="back">
            <button className="btn-back" onClick={() => history.push('/podcast-subscriptions')}>
              <img src={AppPics.BackSvg} alt="Back" />
            </button>
          </li>
        </ul>

        <div className="tab__content ___adjust___tab___padding">
          <div className="tab__content-actHead">
            <AppBreadcrumbs
              links={[
                { label: 'Podcast', to: '/podcast' },
                { label: 'Subscriptions', to: '/podcast-subscriptions' },
              ]}
              current="Details"
            />
          </div>

          <div className="tab__content-actBody">
            <div className="px-5 py-5">
              {detailQuery.isLoading && (
                <div className="text-center py-5">
                  <span className="spinner-border" />
                </div>
              )}

              {detailQuery.isError && (
                <p className="text-center text-danger py-5">Could not load this subscription.</p>
              )}

              {subscription && (
                <>
                  <div className="tab__contentMenu">
                    <h2 className="prime-head p-0 boldtitle text-nowrap">Subscription</h2>
                  </div>

                  {isCancellable && (
                    <div className="d-flex flex-wrap mb-4" style={{ gap: 16 }}>
                      <AppButton
                        variantClass="btn-plane p--tint"
                        style={{ maxWidth: 'none' }}
                        onClick={() => openModal('cancel')}
                      >
                        Cancel subscription
                      </AppButton>
                      <AppButton
                        variantClass="btn-plane p--tint"
                        style={{ maxWidth: 'none' }}
                        onClick={() => openModal('cancelAndRefund')}
                      >
                        Cancel and refund
                      </AppButton>
                    </div>
                  )}

                  <div className="list-group list-group-flush mb-5">
                    {infoRows.map(([label, value]) => (
                      <div key={label} className="list-group-item py-3 px-4 border-0 border-bottom">
                        <div className="row align-items-center">
                          <div className="col-md-3 text-muted">{label}</div>
                          <div
                            className="col-md-9 fw-bold text-dark"
                            style={{ textTransform: 'none' }}
                          >
                            {value || '—'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <h2 className="prime-head p-0 boldtitle mb-3">Transactions</h2>
                  <div className="tableWrap">
                    <div className="table-responsive">
                      <table className="table tableWrap__table">
                        <thead className="thead-light __appTheadColor">
                          <tr>
                            <th className="text-center">Date</th>
                            <th className="text-center">Invoice No</th>
                            <th className="text-center">Plan</th>
                            <th className="text-center">Amount</th>
                            <th className="text-center">Razorpay Payment</th>
                            <th className="text-center">Refund</th>
                            <th className="text-center">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          {payments.length === 0 ? (
                            <tr>
                              <td colSpan={7} className="text-center py-5 text-muted">
                                No transactions yet
                              </td>
                            </tr>
                          ) : (
                            payments.map((payment) => (
                              <tr key={payment._id}>
                                <td className="text-center">{formatDate(payment.paidAt, true)}</td>
                                <td className="text-center">{payment.invoiceNumber || '—'}</td>
                                <td className="text-center" style={{ textTransform: 'capitalize' }}>
                                  {payment.plan}
                                </td>
                                <td className="text-center">
                                  {formatAmount(payment.amount, payment.currency)}
                                </td>
                                <td className="text-center">{payment.providerPaymentId}</td>
                                <td className="text-center">
                                  {payment.refundStatus && payment.refundStatus !== 'none' ? (
                                    <span
                                      className={
                                        REFUND_BADGE[payment.refundStatus] || 'badge bg-secondary'
                                      }
                                      style={{ color: '#fff' }}
                                    >
                                      {payment.refundStatus}
                                    </span>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="text-center">
                                  {payment.refundStatus === 'pending' && (
                                    <AppButton
                                      variantClass="btn-plane p--tint"
                                      onClick={() => openModal('markRefunded', payment)}
                                    >
                                      Mark refunded
                                    </AppButton>
                                  )}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      <AppModal open={!!modal.action} closeModal={closeModal} size="md">
        <AppModalHeader>{MODAL_TITLES[modal.action]}</AppModalHeader>
        <AppModalBody>
          <p className="modal-sub-text text-center">{MODAL_NOTES[modal.action]}</p>
          <textarea
            className="form-control mt-3 mx-auto"
            style={{ width: '88%' }}
            rows={4}
            maxLength={modal.action === 'markRefunded' ? 200 : 500}
            placeholder={modal.action === 'markRefunded' ? 'Refund reference' : 'Reason'}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          {error && <p className="text-danger text-center mt-2 mb-0">{error}</p>}

          <div className="d-flex justify-content-end mt-4 pe-4" style={{ gap: 16 }}>
            <AppButton variantClass="btn-plane p--tint" onClick={closeModal} disabled={saving}>
              Close
            </AppButton>
            <AppButton variantClass="btn-plane p--tint" onClick={submit} disabled={saving}>
              {saving ? 'Please wait…' : 'Confirm'}
            </AppButton>
          </div>
        </AppModalBody>
      </AppModal>
    </div>
  );
}

export default SubscriptionDetail;
